


import {
  defineComponent,
  PropType,
  reactive,
  computed,
  onMounted,
  onBeforeUnmount,
} from '@vue/composition-api';
import { useRunCourseSheets } from '@/composables/useRunCourseSheets';
import { Kilopost } from '@/models/apis/device/adminDeviceResponse';
import type {
  Sheet,
  ReportRouteSlim,
  RunCourseReport,
  RunCourseState,
  RunCourseEditState,
  RunCourseUiState,
  RoutePosition,
  ContextMenuMode,
} from '@/models/runCourse';
import { dtFormat } from '@/lib/dateHelper';
import RouteEditModal from './components/RouteEditModal.vue';
import RunCourseHeader from './components/RunCourseHeader.vue';
import RouteTable from './components/RouteTable.vue';
import DebugInfoSection from './components/DebugInfoSection.vue';
import ContextMenu from './components/ContextMenu.vue';
import {
  initRunCourseState,
  initRunCourseEditState,
  initRunCourseUiState,
} from '@/components/lib/runCourseSheets/utils';
import { NEXT_LINE_REFERENCE } from '@/consts/runCourse';
import type { RunCourseSheetsParameters } from '@/composables/useRunCourseSheets';

export default defineComponent({
  props: {
    group1Id: {
      type: Number,
      required: true,
    },
    report: {
      type: Object as PropType<RunCourseReport>,
      required: true,
    },
    kiloposts: {
      type: Array as PropType<Kilopost[]>,
      required: true,
    },
    maxRowsInPage: {
      type: Number,
      default: 14,
    },
    maxRouteElementsInLine: {
      type: Number,
      default: 3,
    },
  },
  emits: ['save-work-routes'],
  setup(props, { emit }) {
    const runCourseState = reactive<RunCourseState>(initRunCourseState());
    const runCourseEditState = reactive<RunCourseEditState>(initRunCourseEditState());
    const runCourseUiState = reactive<RunCourseUiState>(initRunCourseUiState());

    const activeSheet = computed(() => {
      if (!runCourseState.sheets) return { rows: [] };
      return runCourseState.sheets[runCourseState.activeRouteInfo?.sheetIdx ?? 0];
    });
    const activeRow = computed(() => {
      return activeSheet.value.rows[runCourseState.activeRouteInfo?.rowIdx ?? 0];
    });
    const activeRoute = computed(() => {
      return activeRow.value.routes[runCourseState.activeRouteInfo?.routeIdx ?? 0];
    });

    const runCourseParameters: RunCourseSheetsParameters = {
      group1Id: props.group1Id,
      kps: props.kiloposts,
      report: props.report,
      maxRowsInPage: props.maxRowsInPage,
      maxRouteElementsInLine: props.maxRouteElementsInLine,
    };

    const {
      setRunCourse,
      calcRouteElementsNumRows,
      getRouteConfig,
      getRouteSheets,
      splitSheets,
      getDebugInfo,
    } = useRunCourseSheets(runCourseParameters);

    const getReportDate = computed((): string => {
      const report = props.report;
      if (!report) return '';
      return dtFormat(report.baseDepartureTs, 'yyyy/mm/dd') ?? '';
    });

    const formatReportTimeRange = computed((): string => {
      const report = props.report;
      if (!report) return '';

      const departure = dtFormat(report.baseDepartureTs, 'yyyy/mm/dd HH:MM');
      const arrival = dtFormat(report.baseArrivalTs, 'yyyy/mm/dd HH:MM');
      return `${departure}〜${arrival}`;
    });

    const saveRoute = (route: ReportRouteSlim) => {
      activeRoute.value.startRoadNameDisp = route.startRoadNameDisp;
      activeRoute.value.startTs = route.startTs;
      onBlurInput();
      runCourseEditState.showRouteEditModal = false;
    };

    const startCreate = (params?: RoutePosition) => {
      if (params) {
        runCourseState.activeRouteInfo = params;
      }
      runCourseEditState.isNewRoute = true;
      runCourseEditState.editingRoute = {
        startRoadNameDisp: '',
        startTs: '',
      };
      runCourseEditState.routeType = 'main_line';
      runCourseEditState.showRouteEditModal = true;
    };

    const startEdit = (route: ReportRouteSlim, params: RoutePosition) => {
      runCourseState.activeRouteInfo = params;
      runCourseEditState.routeType = 'main_line';
      if (/on/.test(route.startRoadNameDisp)) {
        runCourseEditState.routeType = 'on';
      } else if (/off/.test(route.startRoadNameDisp)) {
        runCourseEditState.routeType = 'off';
      } else if (/J/.test(route.startRoadNameDisp)) {
        runCourseEditState.routeType = 'jct';
      }
      runCourseState.routeOptions = {};
      if (runCourseEditState.routeType && runCourseState.kpRouteMap) {
        runCourseState.routeOptions = runCourseState.kpRouteMap[runCourseEditState.routeType] ?? {};
      }
      runCourseEditState.isNewRoute = false;
      runCourseEditState.editingRoute = {
        startRoadNameDisp: route.startRoadNameDisp,
        startTs: route.startTs,
        break: route.break,
      };
      runCourseEditState.showRouteEditModal = true;
    };

    const deleteRoute = () => {
      if (!runCourseState.sheets) return;

      activeRow.value.routes.splice((runCourseState.activeRouteInfo?.routeIdx ?? 0), 1);
      // 経路を削除した結果、分割した行の中間行の経路がなくなった場合、行を削除する
      const rowGroup = runCourseState.sheets.flatMap(e => e.rows).filter(e => e.num === activeRow.value.num);
      if (activeRow.value.routes.length === 0 && rowGroup.length > 1) {
        combineRow();
        runCourseEditState.showRouteEditModal = false;
        return;
      }
      setRoutesBreak(activeRow.value.num);
      onBlurInput();
      runCourseEditState.showRouteEditModal = false;
    };

    const showContextmenu = (e: MouseEvent, params: RoutePosition, mode: ContextMenuMode) => {
      if (!(e.target instanceof Element)) return;

      if (!(e.target.classList.contains('route-arrow') || e.target.classList.contains('route-plus'))) {
        return;
      }
      // 2度選択されたらメニューを閉じる
      if (runCourseUiState.showContextMenu) {
        runCourseUiState.showContextMenu = false;
        runCourseUiState.menuMode = '';
        return;
      }
      runCourseUiState.menuMode = mode;
      runCourseUiState.contextMenuPosition = {
        left: e.pageX + 'px',
        top: e.pageY + 'px',
      };
      runCourseUiState.showContextMenu = true;
      // メニュー選択時にはどの場所を選択したか判断できなくなるので
      // activeRouteInfoを保持しておく
      runCourseState.activeRouteInfo = params;
    };

    const updateBikou = (sheetIdx: number, rowIdx: number, value: string) => {
      if (!runCourseState.sheets) return;

      const row = runCourseState.sheets[sheetIdx]?.rows?.[rowIdx];
      if (!row) return;

      row.bikou = value;
    };

    const addRoute = (route: ReportRouteSlim) => {
      if (!runCourseState.sheets) return;
      if (!activeRow.value) return;

      runCourseEditState.editingRoute = { ...route };

      const prevRowNum = calcRouteElementsNumRows(activeRow.value.routes.length);
      activeRow.value.routes.splice((runCourseState.activeRouteInfo?.routeIdx ?? 0) + 1, 0, { ...runCourseEditState.editingRoute });
      // 追加後に1ページ中の経路が最大行数を超える場合は行を分割する
      const rowsInPage = activeSheet.value.rows.reduce((acc, e) => {
        return acc + calcRouteElementsNumRows(e.routes.length);
      }, 0);
      if (rowsInPage > props.maxRowsInPage) {
        if (!runCourseState.activeRouteInfo) return;
        // 対象行の最後の経路要素を次の行に分割する
        runCourseState.activeRouteInfo.routeIdx = activeRow.value.routes.length - 2;
        separateRow();
        runCourseEditState.showRouteEditModal = false;
        return;
      }
      setRoutesBreak(activeRow.value.num);
      const newRowNum = Math.ceil(activeRow.value.routes.length / props.maxRouteElementsInLine);
      if (newRowNum > prevRowNum) {
        runCourseState.sheets = splitSheets(runCourseState.sheets);
      }
      onBlurInput();
      runCourseEditState.showRouteEditModal = false;
    };

    const separateRow = () => {
      if (!runCourseState.sheets || !activeRow.value || !runCourseState.activeRouteInfo) return;

      const currentRowIndex = runCourseState.activeRouteInfo.rowIdx;
      const splitPosition = runCourseState.activeRouteInfo.routeIdx + 1;

      // 分割点以降のルートを取得
      const routesForNewRow = activeRow.value.routes.slice(splitPosition);
      const remainingRoutes = activeRow.value.routes.slice(0, splitPosition);

      // 新しい行を作成
      const newRow = {
        num: activeRow.value.num,
        startTsDisp: routesForNewRow[0]?.startTs || '',
        endTsDisp: activeRow.value.endTsDisp,
        timeSpanDisp: activeRow.value.timeSpanDisp,
        distanceDisp: activeRow.value.distanceDisp,
        bikou: '',
        routes: routesForNewRow,
      };

      // 新しい行を挿入
      activeSheet.value.rows.splice(currentRowIndex + 1, 0, newRow);

      // 元の行を更新
      activeRow.value.routes = remainingRoutes;

      // 分割された行グループの表示を更新
      const rowGroup = runCourseState.sheets.flatMap(s => s.rows).filter(r => r.num === activeRow.value.num);
      rowGroup.forEach((row, index) => {
        const isFirstRow = index === 0;
        const isLastRow = index === rowGroup.length - 1;

        if (!isLastRow) {
          // 最終行以外の処理
          row.endTsDisp = '';
          row.timeSpanDisp = NEXT_LINE_REFERENCE;
          row.distanceDisp = NEXT_LINE_REFERENCE;
          if (!isFirstRow) {
            row.startTsDisp = row.routes[0].startTs;
          }
        } else {
          // 最終行の処理
          row.startTsDisp = '';
        }
      });
      setRoutesBreak(activeRow.value.num);
      runCourseState.sheets = splitSheets(runCourseState.sheets);
      onBlurInput();
    };

    const combineRow = () => {
      if (!runCourseState.sheets || !activeRow.value || !runCourseState.activeRouteInfo) return;

      const currentRowIndex = runCourseState.activeRouteInfo.rowIdx;
      const currentSheetIndex = runCourseState.sheets.indexOf(activeSheet.value);

      // 前の行を取得
      let previousRow = activeSheet.value.rows[currentRowIndex - 1];
      const previousSheet = currentSheetIndex > 0 ? runCourseState.sheets[currentSheetIndex - 1] : null;

      // 現在のシートの最初の行の場合、前のシートの最後の行を取得
      if (!previousRow && previousSheet) {
        previousRow = previousSheet.rows[previousSheet.rows.length - 1];
      }

      if (!previousRow) return;

      // 行の結合
      const lastRouteInPrevRow = previousRow.routes[previousRow.routes.length - 1];
      if (lastRouteInPrevRow) {
        lastRouteInPrevRow.break = undefined;
      }

      // ルートの結合
      previousRow.routes = [...previousRow.routes, ...activeRow.value.routes];
      if (previousRow.bikou !== activeRow.value.bikou) {
        const bikou = [previousRow.bikou, activeRow.value.bikou]
          .filter((value) => value && value.trim() !== '')
          .join(',');
        previousRow.bikou = bikou;
      }
      // 表示情報の更新
      previousRow.endTsDisp = activeRow.value.endTsDisp;
      previousRow.timeSpanDisp = activeRow.value.timeSpanDisp;
      previousRow.distanceDisp = activeRow.value.distanceDisp;

      // 現在の行を削除
      activeSheet.value.rows.splice(currentRowIndex, 1);

      // 結合後の行グループの表示を更新
      const rowGroup = runCourseState.sheets.flatMap(s => s.rows).filter(r => r.num === previousRow.num);
      if (rowGroup.length > 1) {
        const lastRow = rowGroup[rowGroup.length - 1];
        lastRow.startTsDisp = '';
      }

      setRoutesBreak(previousRow.num);
      runCourseState.sheets = splitSheets(runCourseState.sheets);
      onBlurInput();
    };

    const setRoutesBreak = (num: number) => {
      if (!runCourseState.sheets) return;

      // 最初の行をplus,それ以外をellipsisに設定
      // 経路なしの行には設定しない
      const newRowGroup = runCourseState.sheets.flatMap(e => e.rows).filter(e => e.num === num);
      newRowGroup.forEach((row, idx) => {
        if (row.routes.length === 0) return;

        if (idx === 0) {
          row.routes.slice(-1)[0].break = newRowGroup.length > 1 ? 'plus' : undefined;
          return;
        }
        row.routes.slice(-1)[0].break = 'ellipsis';
      });
    };

    const countUpShowDebugInfo = () => {
      runCourseUiState.showDebugInfoCounter++;
      if (runCourseUiState.showDebugInfoCounter < 3) return;

      runCourseUiState.showDebugInfoCounter = 0;
      runCourseUiState.showDebugInfo = true;
    };

    const isLastSheet = (idx: number, list: Sheet[]) => {
      return idx === list.length - 1;
    };

    const loadMtxs = () => {
      setRunCourse();
      const routeConfig = getRouteConfig();
      if (!routeConfig) return;

      runCourseState.kpRouteMap = routeConfig.kpRouteMap;
      runCourseState.routeOptions = routeConfig.routeOptions;
      runCourseState.sheets = getRouteSheets();
      if (!props.report.workRoutes) {
        emit('save-work-routes', props.report.id, runCourseState.sheets);
      }
      const totalMinutes = runCourseState.sheets.reduce((acc, sheet) => {
        return acc + sheet.rows.reduce((rowAcc, row) => {
          if (row.timeSpanDisp === NEXT_LINE_REFERENCE) return rowAcc;
          if (!row.timeSpanDisp) return rowAcc;

          const [hours, minutes] = row.timeSpanDisp.split(':').map(Number);
          return rowAcc + (hours * 60 + minutes);
        }, 0);
      }, 0);

      const totalHours = Math.floor(totalMinutes / 60);
      const remainingMinutes = totalMinutes % 60;

      runCourseState.timeSpanSumDisplay = `${totalHours}:${String(remainingMinutes).padStart(2, '0')}`;
      runCourseState.distanceSumDisplay = runCourseState.sheets.reduce((acc, sheet) => {
        return acc + sheet.rows.reduce((rowAcc, row) => {
          if (row.distanceDisp === NEXT_LINE_REFERENCE) return rowAcc;
          if (!row.distanceDisp) return rowAcc;

          return rowAcc + Number(row.distanceDisp);
        }, 0);
      }, 0).toFixed(2);
      runCourseEditState.routeType = 'main_line';
      runCourseState.debugInfo = getDebugInfo();
    };

    const onBlurInput = () => {
      if (!runCourseState.sheets) return;

      emit('save-work-routes', props.report.id, runCourseState.sheets);
    };

    const handleBodyClick = (e: MouseEvent) => {
      if (!(e.target instanceof Element)) return;

      const isMenuButton = e.target.classList.contains('route-arrow') || e.target.classList.contains('route-plus');
      if (runCourseUiState.showContextMenu && !isMenuButton) {
        runCourseUiState.showContextMenu = false;
        runCourseUiState.menuMode = '';
      }
    };

    onMounted(() => {
      if (Array.isArray(props.report.mtxsExt) && props.report.mtxsExt.length > 0) {
        loadMtxs();
      }
      document.body.addEventListener('click', handleBodyClick);
    });

    onBeforeUnmount(() => {
      document.body.removeEventListener('click', handleBodyClick);
    });

    return {
      runCourseState,
      runCourseEditState,
      runCourseUiState,
      getReportDate,
      formatReportTimeRange,
      addRoute,
      startCreate,
      startEdit,
      deleteRoute,
      saveRoute,
      showContextmenu,
      updateBikou,
      combineRow,
      separateRow,
      onBlurInput,
      countUpShowDebugInfo,
      isLastSheet,
    };
  },
  components: {
    RouteEditModal,
    RunCourseHeader,
    RouteTable,
    DebugInfoSection,
    ContextMenu,
  },
});
